import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "general",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const update = (key) => (eOrValue) => {
    const value = eOrValue?.target ? eOrValue.target.value : eOrValue;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contacts`, form);
      toast.success("Thanks! We will get back to you within 24 hours.");
      setForm(INITIAL);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-pad bg-primary text-primary-foreground relative overflow-hidden">
      <div className="container-outer relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="md:col-span-5"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-accent mb-4">
              Start a conversation
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl font-medium heading-tight text-primary-foreground">
              Let's build your clean-energy roadmap.
            </h2>
            <p className="mt-5 text-base md:text-lg text-primary-foreground/75 leading-relaxed max-w-md">
              Share a few details and one of our project engineers will call
              you back within one business day with a custom proposal.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email"
                className="flex items-center gap-4 group"
              >
                <span className="h-10 w-10 rounded-full border border-primary-foreground/20 flex items-center justify-center group-hover:border-accent transition-colors">
                  <Mail size={16} strokeWidth={1.75} />
                </span>
                <span className="text-base">{BRAND.email}</span>
              </a>
              <a
                href={`tel:${BRAND.phone_tel}`}
                data-testid="contact-phone"
                className="flex items-center gap-4 group"
              >
                <span className="h-10 w-10 rounded-full border border-primary-foreground/20 flex items-center justify-center group-hover:border-accent transition-colors">
                  <Phone size={16} strokeWidth={1.75} />
                </span>
                <span className="text-base">{BRAND.phone}</span>
              </a>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={submit}
            className="md:col-span-7 bg-background text-foreground rounded-lg p-6 md:p-10 border border-border"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="mb-2 block text-sm font-medium">
                  Name
                </Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Doe"
                  className="h-11"
                  data-testid="contact-input-name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="mb-2 block text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="jane@company.com"
                  className="h-11"
                  data-testid="contact-input-email"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="mb-2 block text-sm font-medium">
                  Phone
                </Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 ..."
                  className="h-11"
                  data-testid="contact-input-phone"
                />
              </div>
              <div>
                <Label htmlFor="company" className="mb-2 block text-sm font-medium">
                  Company
                </Label>
                <Input
                  id="company"
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Company name"
                  className="h-11"
                  data-testid="contact-input-company"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="mb-2 block text-sm font-medium">
                  I'm interested in
                </Label>
                <Select value={form.service} onValueChange={update("service")}>
                  <SelectTrigger
                    className="h-11"
                    data-testid="contact-select-service"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ev_charging" data-testid="service-ev">
                      EV Charging Stations
                    </SelectItem>
                    <SelectItem value="solar" data-testid="service-solar">
                      Solar Projects
                    </SelectItem>
                    <SelectItem value="hybrid" data-testid="service-hybrid">
                      Hybrid Inverters
                    </SelectItem>
                    <SelectItem value="ppa" data-testid="service-ppa">
                      Power Purchase Agreement
                    </SelectItem>
                    <SelectItem value="it" data-testid="service-it">
                      IT Products (Scanvio)
                    </SelectItem>
                    <SelectItem value="general" data-testid="service-general">
                      General Inquiry
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="message" className="mb-2 block text-sm font-medium">
                  How can we help?
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your site, load, timelines..."
                  className="min-h-[130px]"
                  data-testid="contact-input-message"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="mt-7 w-full md:w-auto rounded-full h-12 px-8 bg-accent hover:bg-accent/90 text-accent-foreground transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {loading ? "Sending..." : (
                <span className="flex items-center gap-2">
                  Send inquiry <ArrowRight size={16} strokeWidth={2} />
                </span>
              )}
            </Button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
